
import flatpickr from "flatpickr";
const defaultDateFormat = "d/m/Y";

// Options parser: only common options 
// manually convert attribute options to flatpickr option
const returnStringOption = (name: string, value: string) => {
    if (!!value) {
        return { [name]: value };
    }
    return {};
}
const returnDateOption = (name: string, value: string) => {
    let newValue: string | Date = "";
    if (value.length > 0) {
        newValue = (value === "today" ? value : new Date(value));
    }
    return { [name]: newValue };
}
const returnBooleanOption = (name: string, value: string) => {
    if (!!value) {
        return { [name]: value.toLowerCase() === "true" };
    }
    return {};
}
const flatpickrOptions = {
    altformat: (value) => returnStringOption("altFormat", value),
    altinput: (value) => returnBooleanOption("altInput", value),
    altinputclass: (value) => returnStringOption("altInputClass", value),
    allowinput: (value) => returnBooleanOption("allowInput", value),
    dateformat: (value) => returnStringOption("dateformat", value),
    defaultdate: (value) => returnDateOption("defaultDate", value),
    maxdate: (value) => returnDateOption("maxDate", value),
    mindate: (value) => returnDateOption("minDate", value),
    showmonths: (value) => returnStringOption("showMonths", value),
    // add more if needed
};

export default class FlatpickrControl {
    // this will initiate flatpickr date picker
    // inline options data-flatpickr-<option>
    // type of date picker depends on attributes: data-flatpickr, data-flatpickr-time, and data-flatpickr-range-with
    // data-flatpickr-range-with="#endDateId": this will indicate the enddate picker id of the range in order to apply related logic
    // options https://flatpickr.js.org/options/

    private element: HTMLElement;
    private changeCallback: (date, dateString) => {};
    private endDateChangeCallback: (date, dateString) => {};
    public currentDatePickr;
    public endDateInstance: FlatpickrControl;
    private startInput: HTMLInputElement;
    private endInput: HTMLInputElement;

    private _isDatePicker = () => {
        return this.element.hasAttribute("data-flatpickr");
    }
    private _isTimePicker = () => {
        return this.element.hasAttribute("data-flatpickr-time");
    }
    private _isDateRangeWithPicker = () => {
        return this.element.hasAttribute("data-flatpickr-range-with");
    }
    private _isDateRangePicker = () => {
        return this.element.hasAttribute("data-flatpickr-range");
    }

    constructor(element: HTMLElement, changeCallback: (date, dateString) => {} = undefined, endDateChangeCallback: (date, dateString) => {} = undefined) {
        this.element = element;
        this.changeCallback = changeCallback;
        this.endDateChangeCallback = endDateChangeCallback;
    }

    init = (overrideOptions = {}) => {
        let dateFormat = defaultDateFormat;

        // get inline options
        const inlineOptions = parseAllPickrOptions(this.element);

        // date picker
        let options: object = {
            allowInput: true, altInput: true, altFormat: dateFormat
        };

        // time picker
        if (this._isTimePicker()) {
            options = {
                allowInput: true, altInput: true, altFormat: dateFormat, enableTime: true, noCalendar: true, time_24hr: true
            };
        }


        // data range picker
        if (this._isDateRangePicker()) {
            options = {
                allowInput: true, altInput: true, altFormat: dateFormat, mode: "range"
            };
            // get start and end input if available
            this.startInput = this.element.querySelector<HTMLInputElement>("input[data-start-input]");
            this.endInput = this.element.querySelector<HTMLInputElement>("input[data-end-input]");

            // set default dates
            if (this.startInput && this.endInput) {
                const start = this.startInput.getAttribute("data-start-input");
                const end = this.endInput.getAttribute("data-end-input");
                // set to calendar
                if (!!start && !!end) {
                    options["defaultDate"] = [this.startInput.getAttribute("data-start-input"), this.endInput.getAttribute("data-end-input")];
                    this.startInput.value = start;
                    this.endInput.value = end;
                }
            }
        }

        // if element is not an input
        // use wrapper behaviour
        const tagName = this.element.tagName.toLowerCase();
        if (tagName !== 'input') {
            options['wrap'] = true;
        }

        const flatpickrOptions = {
            ...options,
            ...inlineOptions,
            ...overrideOptions,
        }

        // date range-with picker
        // init enddate
        if (this._isDateRangeWithPicker() && this.element.getAttribute("data-flatpickr-range-with").length > 0) {
            this.endDateInstance = new FlatpickrControl(document.querySelector<HTMLElement>(this.element.getAttribute("data-flatpickr-range-with")), this.endDateChangeCallback);
            this.endDateInstance.init();
            if (flatpickrOptions.defaultDate) {
                this.endDateInstance.currentDatePickr.set("minDate", flatpickrOptions.defaultDate);
            }
        }

        // init
        this.currentDatePickr = flatpickr(this.element, {
            ...flatpickrOptions,
            onChange: (date, dateString) => {
                if (this.changeCallback) {
                    this.changeCallback(date, dateString);
                }

                // trigger date change event
                this.element.dispatchEvent(new CustomEvent("datechange", {
                    detail: {
                        date,
                        dateString
                    }
                }));

                // on change: set min date to end date
                if (this.endDateInstance) {
                    this.endDateInstance.currentDatePickr.set("minDate", dateString);
                }

                // set start & end input
                if (this.startInput) {
                    this.startInput.value = date[0] ? flatpickr.formatDate(date[0], "Y-m-d") : "";
                }
                if (this.endInput) {
                    this.endInput.value = date[1] ? flatpickr.formatDate(date[1], "Y-m-d") : "";
                }
            },
            onReady: (...arg) => {
                const instance = arg[2];
                // move calendar to proper month on input
                instance.altInput.addEventListener('input', (e: InputEvent) => {
                    const projectedDate = instance.parseDate(instance.altInput.value, dateFormat);
                    if (projectedDate) {
                        // check if input value is correct
                        const projectedDateString = instance.formatDate(projectedDate, dateFormat);
                        if (projectedDateString === instance.altInput.value) {
                            // date string is valid
                            instance.setDate(instance.altInput.value, false, dateFormat);
                        } else {
                            instance.jumpToDate(projectedDate);
                        }
                    }
                });
                // apply date on altinput change
                instance.altInput.addEventListener('change', (e: InputEvent) => {
                    instance.setDate(instance.altInput.value, true, dateFormat);
                });
            }
        });
        return this.currentDatePickr;
    }
}


function parseAllPickrOptions(element: HTMLElement) {
    let options = {};
    [].slice.call(element.attributes).forEach(attr => {
        if (attr.name.startsWith("data-flatpickr-")) {
            const name = attr.name.substring("data-flatpickr-".length);
            const value = attr.value;
            options = {
                ...options,
                ...convertToFlatPickrOption(name, value)
            }
        }
    });
    return options;
}

function convertToFlatPickrOption(name: string, value: string) {
    if (flatpickrOptions[name]) {
        return flatpickrOptions[name](value);
    } else {
        return {};
    }
}